/**
 * The Overview's daily time series — slice 20, "the Reach dashboard".
 *
 * `GET /overview` answers *what is this workspace's sending doing right now*.
 * This file answers the question a chart asks instead: **what has it been doing
 * for the last thirty days, one day at a time** — and it is written almost
 * entirely to stop a chart from saying something the underlying data does not.
 *
 * ## WHY THIS FILE EXISTS AT ALL, AND WHY IT WAS BUILT LAST
 *
 * Slice 0 left the Overview a placeholder with a sentence slice 10 then quoted
 * back: *"inventing a dashboard out of numbers that do not exist is how a demo
 * starts lying."* A chart is the sharpest form of that lie available, because a
 * chart interpolates. Give a line chart thirty absent days and it draws a
 * confident flat line along the baseline; give it a workspace created last
 * Tuesday and it draws three weeks of perfect quiet before the workspace
 * existed. Nobody reading it can tell that apart from a month of clean sending
 * at zero volume, and both readings are wrong.
 *
 * **So a day is not a number here. It is a discriminated union**, and the arm
 * that means "there was nothing to observe" has no field a number could live in
 * — the same device `engagementPanelSchema` uses in `./engagement.js` for a
 * workspace with tracking off, applied thirty times per chart instead of once
 * per panel. See {@link emailDailyBucketSchema}.
 *
 * ## THE THREE THINGS A DAY CAN BE, AND WHY THREE
 *
 *   - **`before_workspace`** — this UTC day had already ended when the workspace
 *     was created. Nothing could have happened. It carries **no counts at all**,
 *     and {@link emailDailyValues} maps it to `null`, which a chart must draw as
 *     *no mark* — not a zero, not a gap in a line that then reconnects across
 *     it. The axis still shows the day, because a reader comparing this
 *     workspace's month to a calendar needs the day to be there; what is absent
 *     is the mark, not the label.
 *   - **`no_activity`** — the workspace existed and accepted nothing on this
 *     channel that day. **This is a real zero and is drawn as a real zero.** It
 *     carries `accepted: 0` as a literal and no outcome fields, because a day
 *     with no cohort has nothing that could have been delivered, bounced or be
 *     awaiting a result. A `delivered: 0` beside an `accepted: 0` invites a 0%
 *     delivery day, which is `rateReading`'s whole argument at day scale.
 *   - **`activity`** — `accepted >= 1`, with the outcome split. The floor of 1
 *     is on the schema rather than in a comment so the two arms are genuinely
 *     exclusive: an `activity` bucket carrying `accepted: 0` is unparseable, and
 *     that is what stops the flat-line-at-zero lie sneaking back in through the
 *     arm that *does* have number fields.
 *
 * A fourth candidate was rejected: *"a day whose outcomes have not settled."*
 * That is not a state of the day, it is a state of some of its messages, and
 * every recent day is partly in it. It rides as `awaiting` on the activity arm
 * instead, so the chart can show the settling as a segment rather than pretend
 * today's send collapsed. Making it a bucket state would have deleted today's
 * accepted count — the one number on the chart that is complete.
 *
 * **It is still not a state, but its extreme is no longer drawn as a zero.** A
 * day on which *every* accepted message is awaiting has, for its outcome
 * measures, nothing to draw: {@link emailDailyValues} answers `null` there for
 * delivered, bounced and undeliverable — and for complained unless a complaint
 * was reported, which is a count of its own — while `accepted` stays a number. The first version drew those as real zeros, and on a deployment where
 * no delivery event has ever arrived that is a month of "nothing bounced" under
 * Amazon's line. `noOutcomeReported` in `./overview.js` asks the same question of
 * a whole window.
 *
 * ## THE BUCKETING INSTANT: `messages.sent_at`, TRUNCATED IN UTC
 *
 * A message lands in the UTC day containing its **acceptance stamp** — the
 * instant a provider took it, which `SendService.send` writes and which stays
 * null on anything no provider took. Three consequences, and they are the point:
 *
 *   1. **A message has exactly one `sent_at`, so it has exactly one bucket.**
 *      That is structural, not asserted. `analytics-campaigns.ts` argues the
 *      same property one level up for whole campaigns and had to work for it,
 *      because `campaigns` has no send instant and it had to choose between
 *      three candidates. Here there is one column and no choice to get wrong.
 *   2. **A day's outcomes belong to the day the messages were accepted, not to
 *      the day the notification arrived.** A bounce that lands this morning
 *      about a message accepted last Tuesday is drawn on Tuesday. That is
 *      `overview-window.ts`'s cohort rule, and without it a day's segments can
 *      exceed the day's own bar — the mechanism by which a tenant who has
 *      stopped sending watches their bounce rate climb.
 *   3. **It deliberately diverges from `analytics-campaigns.ts`, which buckets
 *      by `min(messages.sent_at)`, and from `overview-campaigns.ts`, which
 *      reports `max(messages.sent_at)`.** Those two bucket and stamp *campaigns*
 *      and are answering "when did this send begin" and "when did it finish".
 *      This buckets *messages*. A campaign that starts at 23:50 UTC and runs
 *      past midnight is entirely one campaign in September on the analytics
 *      report and is split across two bars here — correctly, because the bars
 *      are messages and the messages really were accepted on two days. Anybody
 *      comparing the two screens should expect that, which is why it is written
 *      down rather than left to be discovered as an inconsistency.
 *
 * **UTC, and not the viewer's browser zone.** Analytics elsewhere in this app
 * resolves a range in a named zone the tenant picks, which is right for a report
 * somebody chose the boundaries of. It is wrong here: this endpoint takes no
 * parameters, so there is no zone anybody chose, and the alternative — the
 * browser's own zone — means the same workspace's dashboard shows different bars
 * to a colleague in London and a colleague in Mumbai, with nothing on screen
 * saying so. A reader who cannot tell which midnight a bar starts at cannot act
 * on the bar. UTC is a literal on the wire ({@link OVERVIEW_ACTIVITY_TIME_ZONE})
 * rather than a convention, and {@link OVERVIEW_ACTIVITY_TIME_ZONE_COPY} carries
 * the axis label the chart must print, so "which midnight" is answered on the
 * chart and not in a tooltip.
 *
 * ## COUNTS, NEVER RATES — AND THERE IS NOWHERE TO PUT ONE
 *
 * There is no rate field on any bucket, and adding one would repeal
 * {@link OVERVIEW_MIN_VOLUME_FOR_RATE} thirty times over. That floor is 1,000
 * messages in a rate's denominator — reported outcomes, since slice 19 — because
 * AWS's complaint threshold is 0.1% and a rate that cannot be *distinguished
 * from the line* is not a rate. A single day almost
 * never clears it — a workspace hitting the floor over the whole window averages
 * 33 messages a day — so thirty honest daily rates would be thirty `null`s, and
 * the pressure would immediately be to lower the floor "just for the chart".
 * That is exactly how day 14 with three messages renders 33%.
 *
 * **What a chart draws against a threshold instead is a count line.** The
 * headline rate and its AWS threshold live on `GET /overview`, where they are
 * computed over a denominator big enough to mean something; a chart of daily
 * counts carries a threshold as *"how many of this day's messages would have
 * been 5%"* — see {@link dailyThresholdCount} — and only on a day with at least
 * that many reported outcomes. Below it there is no line, for the reason there
 * is no rate: the first version drew one at 0.15 of a message over a
 * three-message day, and a bar ten times its height said "over Amazon's line" as
 * plainly as a percentage would. {@link NO_DAILY_RATE_COPY} is the short line
 * that says so on screen.
 *
 * ## `complained` IS A SUBSET OF `delivered` AND MUST NEVER BE STACKED
 *
 * A complaint follows a delivery: somebody received the message and pressed
 * *this is spam*. `delivered + bounced + complained` therefore counts those
 * messages twice and produces a bar taller than the day's own `accepted`. It is
 * on the activity arm because it is the number that costs a tenant their AWS
 * account and it belongs on this screen; it is documented here as an overlay
 * rather than a segment because a stacked bar is the obvious thing to build and
 * it is wrong. `accepted = delivered + bounced + awaiting + (messages that
 * failed before a provider ever answered)` is the identity that does hold.
 *
 * ## ENGAGEMENT AND READ RECEIPTS ARE ABSENT, STRUCTURALLY
 *
 * No `opened`, no `clicked`, no WhatsApp `read` — not as a field, not as a
 * nullable one. `HomePage.tsx`'s header states both rules and slice 20 does not
 * relax either: an engagement number appears only for a workspace with tracking
 * on, and it is served by `GET /overview/engagement` where the untracked arm has
 * nowhere for a number to live. A daily engagement series would have to invent a
 * per-day version of that union, and thirty directional numbers drawn at the
 * same rank as thirty exact ones is the rank confusion `EngagementRate` and
 * `OverviewRateKpi` were made mutually non-assignable to prevent. WhatsApp read
 * receipts stay off for the narrower reason: a read floor has no threshold, no
 * verdict and no direction anybody can act on, and this is the screen where
 * sending health is read.
 *
 * ## WHY A SECOND ROUTE, AND NOT A MEMBER OF `overviewResponseSchema`
 *
 * `overviewResponseSchema` is `.strict()` and its header says, at the place
 * somebody would try it, that adding a member repeals a stated property in one
 * line. That property is worth keeping: it is what makes "does drawing the
 * dashboard call AWS" answerable by reading the imports. This is thirty times
 * the row count of that response, it is grouped over a monthly-partitioned
 * table, and it is the part of the screen that can afford to arrive second.
 * `GET /overview/activity` is therefore additive: the tiles render from the
 * existing response and the charts fill in beneath them, and neither can fail
 * the other — the same separation slice 11 made for the engagement panel and for
 * the same reason.
 *
 * ## THE WINDOW IS THE OVERVIEW'S WINDOW, TO THE MILLISECOND
 *
 * `[since, until)` with the same `OVERVIEW_ACTIVITY_WINDOW_DAYS` and the same
 * arithmetic, so **the daily `accepted` counts sum to the headline `accepted`**
 * on the email series. That invariant is the whole reason to reuse the bounds
 * rather than round to thirty whole UTC days: a chart whose bars do not add up
 * to the number printed above them teaches the reader that one of the two is
 * approximate, and neither is.
 *
 * The price is that a rolling thirty-day window starting mid-day spans
 * **thirty-one** calendar days, and the first and last of them are partly
 * covered. That is paid honestly rather than hidden: `coverage` marks them, the
 * chart draws them differently, and {@link PARTIAL_DAY_COPY} says why in a
 * short line. Silently dropping the two edge buckets would restore a round thirty
 * bars and break the sum; silently drawing them at full weight is how a
 * dashboard reports "sending collapsed today" at 09:00 UTC.
 *
 * ## THERE IS NO PROVIDER SNAPSHOT HERE, AND THAT IS NOT AN OMISSION
 *
 * `overviewSesSendabilitySchema` and its WhatsApp counterpart each carry an
 * {@link OverviewSnapshot} saying how old the stored provider check is, because
 * every fact on those cards is a copy of something AWS or Meta said, and
 * repeating it without its age is how a screen invents an enforcement action out
 * of a stale boolean. **Nothing in this file is a provider fact.** Every count
 * comes from `messages` and `delivery_events` in this database, read inside the
 * request that returns it, so `generatedAt` is the whole of its provenance and a
 * staleness field would be a second thing to keep in step with nothing.
 *
 * ## WHAT IS NOT A TIME SERIES AND IS HERE ANYWAY
 *
 * {@link overviewAttentionSchema} — the campaigns that have **stopped
 * themselves and are waiting for a person**. Slice 18 made that reachable and it
 * is the single most urgent thing this screen can say, so it is read at the same
 * instant as the charts rather than inferred from the five-row recent-campaigns
 * list, where a campaign halted three weeks ago has already fallen off. It
 * imports slice 18's own vocabulary — `CAMPAIGN_HALT_REASON_VALUES` — rather
 * than restating it, so a halt cannot be described here in words the campaign
 * screen does not use.
 *
 * ## IMPORT DIRECTION
 *
 * This is a **leaf**. It imports `../enums.js` for the channel and campaign-
 * status vocabularies, `../common.js` for the instant, `./overview.js` for the
 * window shape and the activity-window constant, `./analytics.js` for
 * `calendarDateSchema` — one definition of what a `YYYY-MM-DD` is, rather than a
 * second regex that accepts 2026-02-30 — and `./list-safety.js` for the halt
 * reasons. Five arrows, all pointing down, and nothing below points back. Two
 * modules that both build zod schemas at load cannot be a cycle, and the failure
 * when they are shows up as `z.enum(undefined)` at import rather than as a
 * review comment.
 *
 * `./chart.js` sits **above** this file and imports it. That split is slice 15's
 * `onboarding.ts` / `navigation.ts` argument: only one of the two is a payload.
 * Everything here is parsed off the wire against a `.strict()` schema; nothing
 * in `./chart.js` ever leaves the browser.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { channelSchema, type CampaignStatus, type Channel } from '../enums.js';
import { calendarDateSchema, type CalendarDate } from './analytics.js';
import {
  CAMPAIGN_HALT_REASON_VALUES,
  campaignHaltReasonSchema,
  type CampaignHaltReason,
} from './list-safety.js';
import {
  OVERVIEW_ACTIVITY_WINDOW_DAYS,
  OVERVIEW_MIN_VOLUME_FOR_RATE,
  onlyBouncesReported,
  overviewWindowSchema,
} from './overview.js';

/** One UTC day, in milliseconds. Days are a fixed length in UTC — no DST to lose. */
const MS_PER_DAY = 24 * 60 * 60 * 1_000;

/* -------------------------------------------------------------------------- */
/* The bucket unit, and the zone it is truncated in                            */
/* -------------------------------------------------------------------------- */

/**
 * One bar is one day. Not one hour, not one week.
 *
 * An hourly series over thirty days is 720 marks nobody can read and a
 * denominator per mark small enough that every one of them is noise. A weekly
 * series is four bars, which cannot show the shape this screen exists to show:
 * a bounce problem starts on one afternoon and a weekly bar hides it inside six
 * good days. A day is also the grain a person schedules sending at, so a bar the
 * reader can act on maps to a decision they already make.
 */
export const OVERVIEW_ACTIVITY_BUCKET_UNIT = 'day' as const;

/**
 * Days are truncated in **UTC** and nothing else, and this is a literal on the
 * wire so a component cannot claim otherwise.
 *
 * The rejected alternative is the viewer's browser zone, which is what analytics
 * elsewhere in this app uses and which is a known open item there rather than a
 * pattern to copy. It fails in a specific way that leaves no trace: two people
 * in one workspace open the same dashboard from Mumbai and London, see bars of
 * different heights on days with the same label, and neither screen says a zone
 * was involved. India is **+05:30 and never anything else**, so the two
 * conventions disagree for the first five and a half hours of every Indian day
 * and agree for the other eighteen and a half: a message accepted at 04:00 IST
 * is 22:30 UTC — the *previous* UTC day — while one accepted at 23:50 IST is
 * 18:20 UTC on the *same* day and moves nothing. Whichever convention is
 * chosen, the only defensible thing is to name it where the bars are, and the
 * worked example printed beside them has to be an hour that actually crosses.
 * An earlier draft of this file and of {@link OVERVIEW_ACTIVITY_TIME_ZONE_COPY}
 * used 23:50 IST as that example, which is a true-sounding sentence about a
 * message that does not change bars at all; `verify-overview-series.ts` now
 * parses the hour out of the disclosure and checks it against the offset, so
 * the example cannot go wrong again without failing.
 *
 * IST was not chosen either, despite every tenant in this database being Indian.
 * `delivery_events.period` is pinned by CHECK to `date_trunc('month', occurred_at
 * at time zone 'UTC')`, `monthStartOf` in `overview-window.ts` builds its
 * partition floor through UTC getters for exactly that reason, and a series that
 * bucketed in a second zone would put the day boundary and the partition
 * boundary in different places. One zone, chosen once, and it is the one the
 * database already stores by.
 */
export const OVERVIEW_ACTIVITY_TIME_ZONE = 'UTC' as const;

/**
 * How the chart says "UTC" — on the chart, not in a tooltip.
 *
 * `axisLabel` is not decoration and is not optional. It is the x-axis title, and
 * a chart that renders the bars without it is drawing days whose start nobody
 * can name. It is a constant rather than a string in a component so that the
 * axis and {@link OVERVIEW_ACTIVITY_TIME_ZONE} cannot come apart, which is the
 * `windowDays`-printed-from-a-second-literal failure `overview-window.ts`
 * documents at length.
 *
 * `short` and `detail` are the text rule applied: four words a skimmer reads,
 * and the sentence behind a disclosure for the reader who wants to know why.
 * Progressive does not mean the detail is deleted — slice 16 made that mistake
 * once and left a replace-token screen with three fields and no instructions.
 *
 * **The worked example in `detail` is load-bearing and was wrong once.** It
 * named 11:50 pm in India, which is 18:20 UTC on the *same* day and therefore an
 * example of a message that does not move at all — a sentence that reads as an
 * explanation and teaches the reader the opposite of the rule. India is +05:30
 * with no DST, so the hours that genuinely land on the previous UTC day are
 * 00:00 to 05:29, and the example has to come from that band. There is nothing
 * about that a type can hold, so `verify-overview-series.ts` extracts the hour
 * from this string and checks it against the offset instead.
 */
export const OVERVIEW_ACTIVITY_TIME_ZONE_COPY = {
  /** The x-axis title. Always rendered. */
  axisLabel: 'Day (UTC)',
  /** Beside the chart, always visible. */
  short: 'Days start at midnight UTC.',
  /** Behind a disclosure. */
  detail:
    'Every bar covers one day from midnight to midnight UTC, not midnight where you are. India runs five and a half hours ahead, so a message accepted at 4 am in India is counted on the previous UTC day; anything sent later in the Indian day lands on the day it looks like. The whole workspace sees the same bars whichever country it opens them from.',
} as const;

/* -------------------------------------------------------------------------- */
/* A day, in three states                                                      */
/* -------------------------------------------------------------------------- */

/**
 * What a bucket is, and the reason there are exactly three of them.
 *
 * These are not severities and not a scale. They answer *was there anything to
 * observe, and did anything happen*, in that order, and the first question has
 * to be asked first because a workspace that did not exist cannot have been
 * quiet.
 */
export const DAILY_BUCKET_STATE_VALUES = ['before_workspace', 'no_activity', 'activity'] as const;
export const dailyBucketStateSchema = z.enum(DAILY_BUCKET_STATE_VALUES);
export type DailyBucketState = z.infer<typeof dailyBucketStateSchema>;

/**
 * Whether a bucket covers a whole UTC day or only part of one.
 *
 * Three separate causes fold into one field, deliberately, because they produce
 * the same reading error and want the same sentence: the window opens mid-day
 * (the oldest bucket), the window closes mid-day (today, always), or the
 * workspace was created mid-day (the bucket it was created in). In every case
 * the bar is short because less time was observed, not because less was sent,
 * and a reader who is not told that concludes the wrong thing from a true
 * number.
 *
 * Three flags were rejected: nothing on the screen would treat them
 * differently, and a component that has to remember to check all three will
 * check two.
 */
export const DAILY_BUCKET_COVERAGE_VALUES = ['whole_day', 'partial_day'] as const;
export const dailyBucketCoverageSchema = z.enum(DAILY_BUCKET_COVERAGE_VALUES);
export type DailyBucketCoverage = z.infer<typeof dailyBucketCoverageSchema>;

/**
 * What the chart says about a day that predates the workspace. A few words, then
 * the reason.
 *
 * The short form names what it is about. It was "Before this workspace
 * existed." — a fragment with no referent, printed in a line of notes under a
 * thirty-one-day chart, which left the reader to work out which days it meant.
 * Review found that; the blank days are the referent, and the sentence says so
 * inside the forty characters a note beside a chart gets.
 */
export const BEFORE_WORKSPACE_COPY = {
  short: 'Blank days predate this workspace.',
  detail:
    'This workspace was created part-way through the period the chart covers. The days before that are shown on the axis so the dates line up with a calendar, but nothing is drawn on them — there was nothing that could have been sent.',
} as const;

/**
 * "Last 7 days" — the trailing slice of the same thirty daily buckets, and the
 * sentences that keep it honest.
 *
 * WHY THIS IS A SLICE AND NOT A SECOND REQUEST
 * ---------------------------------------------------------------------------
 * `enumerateUtcDays` emits an entry for **every** UTC day the window touches,
 * including days nothing was sent on. That is what makes a contiguous trailing
 * run of seven buckets a complete sub-window rather than a sample of one: there
 * is no day missing from it that a `GROUP BY` would have dropped. A second
 * endpoint windowed to seven days would return the same seven numbers and cost
 * a second query.
 *
 * It is the one fold a component may do over these buckets, and the totals
 * schema's own justification — "carried so a component never sums them itself"
 * — still holds for the thirty-day figures, which is why `totals` is not the
 * thing being sliced.
 *
 * WHY THERE IS NO WEEK-OVER-WEEK COMPARISON HERE
 * ---------------------------------------------------------------------------
 * The obvious next thing is "and 47 a day the week before". It is refused. A
 * per-day mean over a series that arrives in campaigns describes no day that
 * happened, and a delta needs a floor below which it is noise — a third floor
 * on a page that already has {@link OVERVIEW_MIN_VOLUME_FOR_RATE} and the
 * engagement one, teaching its reader that floors are arbitrary. The card shows
 * what went out; it does not narrate a trend it cannot carry.
 *
 * And there is no period control, for the reason the activity controller gives
 * for having no window parameter: a reader who can choose the window can choose
 * the window in which their bounce rate looks acceptable.
 */
export const OVERVIEW_RECENT_DAYS = 7;

export const RECENT_DAYS_COPY = {
  span: `The last ${String(OVERVIEW_RECENT_DAYS)} UTC days.`,
  /**
   * Never "today". At UTC+05:30 the newest UTC bucket is the day the reader
   * calls yesterday for five and a half hours out of every twenty-four, which
   * is exactly the defect the calendar shipped and slice 33 had to fix.
   */
  stillRunning: 'The newest is still running.',
  nothingSent: `Nothing went out in these ${String(OVERVIEW_RECENT_DAYS)} days.`,
  notMeasured: 'This workspace did not exist on any of these days.',
  beforeWorkspaceTotal: 'Those days are left out of the total.',
  createdInside:
    'The day this workspace was created is short for the same reason: less of it was observed, not less was sent.',
  /**
   * {@link PARTIAL_DAY_COPY.detail} cannot be reused for this. It says the
   * first bar begins "exactly 30 days before you loaded this screen", which is
   * false of a trailing slice — the seven days begin twenty-three days after
   * the window does.
   */
  detail: `These are the ${String(OVERVIEW_RECENT_DAYS)} most recent UTC days of the ${String(OVERVIEW_ACTIVITY_WINDOW_DAYS)} this page reads. The newest is the UTC day the figures were read in, and it is still filling, so its total keeps growing until midnight UTC — which is not midnight where you are. The days together are not a fixed ${String(OVERVIEW_RECENT_DAYS)} times twenty-four hours, and nothing on this card compares one week with another.`,
} as const;

/**
 * What the chart says about a bucket whose day is only partly covered.
 *
 * "The first and last bars", and neither "only part of this day" (the first
 * wording, which named no day) nor "the oldest bar": for a workspace created
 * inside the window the oldest slots are blank and the short bar is the first
 * one *drawn*, the day it was created. The detail says which is which.
 */
export const PARTIAL_DAY_COPY = {
  short: 'First and last bars cover part of a day.',
  detail: `Two bars can cover less than a full day. The last is today, which is still running, so its bar keeps growing — compare it with the same time yesterday rather than with a finished day. The first bar drawn starts part-way through its day: at the moment this period begins, exactly ${OVERVIEW_ACTIVITY_WINDOW_DAYS} days before you loaded this screen, or at the moment this workspace was created, if that was later.`,
} as const;

/** Why the recent bars look worse than the older ones, and why that is not a problem. */
export const AWAITING_OUTCOMES_COPY = {
  short: 'Recent days are still settling.',
  detail:
    'Amazon and Meta report what happened to a message minutes to hours after it was accepted, and a bounce can arrive a day later. The most recent days therefore include messages still waiting for a result. They are not failures, and they fill in as the reports arrive — except email sent before delivery reports were set up, which never gets one.',
} as const;

/**
 * Why there is no percentage on any bar.
 *
 * The floor is interpolated from {@link OVERVIEW_MIN_VOLUME_FOR_RATE} rather
 * than typed into the sentence, because a sentence naming a number the code does
 * not use is the caption-goes-stale failure this codebase has documented three
 * times. `toLocaleString('en-GB')` gives the grouping a reader expects — 1,000,
 * not 1000 — from the same locale the rest of the product formats counts in.
 */
export const NO_DAILY_RATE_COPY = {
  short: 'Counts, not percentages.',
  detail: `A bounce rate over one day is noise wearing a percentage sign: three messages and one bounce is 33%, and it means nothing. Wend only prints a percentage over at least ${OVERVIEW_MIN_VOLUME_FOR_RATE.toLocaleString('en-GB')} messages with a delivery or a bounce reported, and draws Amazon's line only on a day that clears the same floor. Where it is drawn, the dashed line is the count that would have been Amazon's review rate over that day's reported outcomes — Amazon reviews an account's rates, not one day's.`,
} as const;

/**
 * A day before the workspace was created. **No counts, and there is nowhere to
 * put one.**
 *
 * This is the arm that makes the whole file worth writing. A chart handed a
 * `no_activity` bucket draws a zero, correctly; handed this one it must draw
 * nothing, and the only way to be sure it does is to give it nothing to draw.
 * A `null` count field would have worked at runtime and failed in review, the
 * way every nullable-that-means-two-things does: somebody writes `?? 0`.
 */
export const beforeWorkspaceBucketSchema = z
  .object({
    state: z.literal('before_workspace'),
    /** The UTC calendar day, `YYYY-MM-DD`. Still on the axis; just not drawn. */
    date: calendarDateSchema,
  })
  .strict();
export type BeforeWorkspaceBucket = z.infer<typeof beforeWorkspaceBucketSchema>;

/**
 * A day the workspace existed for and accepted nothing on this channel.
 *
 * `accepted` is `z.literal(0)` rather than absent, because **this zero is real
 * and must be drawn.** "Nothing sent yet" and "0% bounce rate" are different
 * statements and only one is ever true — but "nothing sent on Tuesday" is a
 * true statement about Tuesday, and a chart that skipped it would leave a gap
 * that reads as missing data.
 *
 * There are no outcome fields, and that is the same argument in the other
 * direction. A day with no cohort has nothing that could have been delivered or
 * bounced, so a `delivered: 0` here would be the numerator of a 0% delivery day
 * — a percentage over an empty denominator, which is the one thing
 * `rateReading` exists to refuse.
 */
export const noActivityBucketSchema = z
  .object({
    state: z.literal('no_activity'),
    date: calendarDateSchema,
    coverage: dailyBucketCoverageSchema,
    /** Exactly zero, by the schema. A real zero, drawn as a real zero. */
    accepted: z.literal(0),
  })
  .strict();
export type NoActivityBucket = z.infer<typeof noActivityBucketSchema>;

/**
 * A day this workspace sent email on.
 *
 * `accepted` has `min(1)`, which is what makes this arm and
 * {@link noActivityBucketSchema} genuinely exclusive rather than
 * conventionally so: an `activity` bucket with `accepted: 0` does not parse, so
 * the flat-line-at-zero cannot come back through the arm that has number fields
 * in it.
 *
 * **`complained` is a subset of `delivered` and is never a stack segment.** See
 * the file header. It is here because it is the number AWS reviews an account
 * against and this screen exists to make it unavoidable; it is drawn as an
 * overlay or a second small chart, never as a third colour on the same bar.
 *
 * `awaiting` is the day's accepted messages with no terminal status yet, and it
 * is the field that keeps today's bar honest: without it the most recent day
 * looks like a collapse in delivery rather than a normal wait for SNS.
 *
 * Every count here counts **messages**, not provider notifications —
 * `count(distinct message_id)` over `delivery_events`, or the message's own
 * status column. SES sends as many rows as it likes about one message and slices
 * 10, 11 and 12 each shipped a version of that defect. At day scale it is worse
 * than at window scale, because one duplicated bounce is a visible spike on a
 * chart somebody is reading for spikes.
 */
export const emailActivityBucketSchema = z
  .object({
    state: z.literal('activity'),
    date: calendarDateSchema,
    coverage: dailyBucketCoverageSchema,
    /** Email SES accepted on this UTC day. At least one, by the schema. */
    accepted: z.number().int().min(1),
    delivered: z.number().int().min(0),
    bounced: z.number().int().min(0),
    /**
     * Messages reported as spam. **A subset of `delivered`, not a peer of it.**
     * Never summed with `delivered` and `bounced`.
     */
    complained: z.number().int().min(0),
    /**
     * Accepted, no terminal status yet. The number that should trend to zero —
     * except for email sent before delivery reports were set up, which is never
     * reported on and stays here until its day leaves the window.
     */
    awaiting: z.number().int().min(0),
  })
  .strict();
export type EmailActivityBucket = z.infer<typeof emailActivityBucketSchema>;

/**
 * A day this workspace sent WhatsApp on.
 *
 * **The numerator field is `undeliverable`, not `bounced`, and that is a
 * correctness barrier rather than a naming preference.** There is no bounce on
 * WhatsApp: Meta reports `failed` with an error code, and a rate limit, a policy
 * block and an unregistered number all arrive as that one word. Because the two
 * arms carry different numerator names they are mutually non-assignable, so a
 * WhatsApp bucket cannot be handed to a chart that draws AWS's bounce threshold
 * across it — slice 18's device, and the reason it exists is that slice 10
 * shipped the opposite: a workspace sitting exactly on AWS's 5% line rendered as
 * 1.55% because two thousand WhatsApp sends were in the denominator.
 *
 * There is no `complained`, because Meta has no complaint event, and no `read`,
 * because read receipts stay off this screen — see the file header.
 */
export const whatsAppActivityBucketSchema = z
  .object({
    state: z.literal('activity'),
    date: calendarDateSchema,
    coverage: dailyBucketCoverageSchema,
    /** WhatsApp messages Meta accepted on this UTC day. At least one. */
    accepted: z.number().int().min(1),
    delivered: z.number().int().min(0),
    /** Meta reported `failed`. Never called a bounce. See above. */
    undeliverable: z.number().int().min(0),
    /** Accepted, no terminal status yet. */
    awaiting: z.number().int().min(0),
  })
  .strict();
export type WhatsAppActivityBucket = z.infer<typeof whatsAppActivityBucketSchema>;

/**
 * One day on the email chart, in whichever of its three states it is in.
 *
 * The two absent-ish arms are **shared** with the WhatsApp union below rather
 * than duplicated per channel, because "the workspace did not exist" and "the
 * workspace sent nothing" carry no channel vocabulary at all. Only the arm with
 * outcomes in it differs, which is exactly where the two channels' vocabularies
 * genuinely diverge.
 */
export const emailDailyBucketSchema = z.discriminatedUnion('state', [
  beforeWorkspaceBucketSchema,
  noActivityBucketSchema,
  emailActivityBucketSchema,
]);
export type EmailDailyBucket = z.infer<typeof emailDailyBucketSchema>;

/** One day on the WhatsApp chart. Same three states, Meta's vocabulary. */
export const whatsAppDailyBucketSchema = z.discriminatedUnion('state', [
  beforeWorkspaceBucketSchema,
  noActivityBucketSchema,
  whatsAppActivityBucketSchema,
]);
export type WhatsAppDailyBucket = z.infer<typeof whatsAppDailyBucketSchema>;

/* -------------------------------------------------------------------------- */
/* Turning three states into two drawable values                               */
/* -------------------------------------------------------------------------- */

/**
 * What a chart can plot from an email series.
 *
 * A closed vocabulary rather than a string, so a component cannot ask for a
 * measure the bucket does not carry, and so the label and the field cannot
 * drift apart. `complained` is a member because it is drawn — as its own small
 * chart or an overlay — and its exclusion from any stack is a rule the header
 * states rather than something this enum can enforce.
 */
export const EMAIL_DAILY_MEASURE_VALUES = [
  'accepted',
  'delivered',
  'bounced',
  'complained',
  'awaiting',
] as const;
export const emailDailyMeasureSchema = z.enum(EMAIL_DAILY_MEASURE_VALUES);
export type EmailDailyMeasure = z.infer<typeof emailDailyMeasureSchema>;

/** What a chart can plot from a WhatsApp series. No bounce, no complaint, no read. */
export const WHATSAPP_DAILY_MEASURE_VALUES = [
  'accepted',
  'delivered',
  'undeliverable',
  'awaiting',
] as const;
export const whatsAppDailyMeasureSchema = z.enum(WHATSAPP_DAILY_MEASURE_VALUES);
export type WhatsAppDailyMeasure = z.infer<typeof whatsAppDailyMeasureSchema>;

/** The words on a legend and in a chart's text alternative. */
export const EMAIL_DAILY_MEASURE_LABELS: Readonly<Record<EmailDailyMeasure, string>> = {
  accepted: 'Accepted by Amazon',
  delivered: 'Delivered',
  bounced: 'Bounced',
  complained: 'Reported as spam',
  awaiting: 'Awaiting a result',
};

export const WHATSAPP_DAILY_MEASURE_LABELS: Readonly<Record<WhatsAppDailyMeasure, string>> = {
  accepted: 'Accepted by Meta',
  delivered: 'Delivered',
  undeliverable: 'Could not be delivered',
  awaiting: 'Awaiting a result',
};

/**
 * **The one place the three-way distinction becomes an array a chart can draw,
 * and therefore the one place it can be got wrong.**
 *
 * `null` for `before_workspace` and a number everywhere else. Not `0` for the
 * first — a chart handed `null` must skip the mark entirely and must not join a
 * line across it, and a chart handed `0` draws a point on the baseline that a
 * reader cannot distinguish from a quiet day. That is the lie this slice was
 * deferred until last to avoid, and it is one `??` away at every call site,
 * which is why there is a function rather than a convention.
 *
 * A `no_activity` bucket answers `0` for **every** measure, including the
 * outcome ones it has no fields for. That is correct rather than a fallback: a
 * day that accepted nothing delivered nothing and bounced nothing, and those
 * zeros are facts. What it must never produce is a *rate*, which is why no
 * measure here is a fraction.
 *
 * **An outcome on a day nobody has reported on is `null` too**, and this is the
 * rule the first version of this function did not have. An `activity` day whose
 * every accepted message is still `awaiting` carries `delivered: 0`,
 * `bounced: 0` and `complained: 0` — and those zeros are not facts about the
 * day, they are the absence of any report about it. Mapped straight through,
 * they drew a row of zero stubs under Amazon's dashed line, which is the flat
 * line at zero this slice exists to refuse, arriving through the one arm that
 * *does* have numbers in it. On the live deployment, where SNS is not wired and
 * no event has ever arrived, it was every day of the chart. So the outcome
 * measures — every measure except `accepted` and `awaiting`, which this database
 * knows without asking a provider — answer `null` on such a day, and
 * {@link OUTCOME_MEASURES_KNOWN_WITHOUT_A_REPORT} is the list that decides it.
 * A day with even one outcome back keeps its counts: they are then a true floor,
 * and the settling note under the chart says the rest is still arriving.
 *
 * The array is index-aligned with the bucket array it was built from and has the
 * same length, so a chart's marks, its axis labels and its text alternative all
 * index the same way. Returning a shorter array — filtering the undrawable days
 * out — was rejected: it silently shifts every label by however many days the
 * workspace did not exist for.
 */
export function emailDailyValues(
  buckets: readonly EmailDailyBucket[],
  measure: EmailDailyMeasure,
): (number | null)[] {
  return buckets.map((bucket) => {
    if (bucket.state === 'before_workspace') return null;
    if (bucket.state === 'no_activity') return 0;
    if (!isKnownWithoutAReport(measure) && bucket.awaiting >= bucket.accepted) {
      /*
       * Per measure. A complaint on a day with no delivery or bounce reported is
       * a count of its own and is drawn; the day's delivered and bounced are
       * still not reported, and stay null. Slice 19 first let one complaint turn
       * the whole day's counts back into numbers, which drew "0 bounced" as a
       * fact about a day nobody had reported a bounce or a delivery for (review).
       */
      if (measure === 'complained' && bucket.complained > 0) return bucket.complained;
      return null;
    }
    return bucket[measure];
  });
}

/** The WhatsApp half. Same rule, Meta's measures. See {@link emailDailyValues}. */
export function whatsAppDailyValues(
  buckets: readonly WhatsAppDailyBucket[],
  measure: WhatsAppDailyMeasure,
): (number | null)[] {
  return buckets.map((bucket) => {
    if (bucket.state === 'before_workspace') return null;
    if (bucket.state === 'no_activity') return 0;
    if (!isKnownWithoutAReport(measure) && bucket.awaiting >= bucket.accepted) return null;
    return bucket[measure];
  });
}

/**
 * The two measures this database knows about a day without any provider having
 * reported on it: what was accepted, and how much of that is still waiting.
 * Everything else — delivered, bounced, complained, undeliverable — is a report,
 * and is `null` on a day that has had none. A closed list rather than a negated
 * one, so a sixth measure added later is treated as a report until somebody
 * decides otherwise, which is the direction that cannot draw a false zero.
 */
export const OUTCOME_MEASURES_KNOWN_WITHOUT_A_REPORT = ['accepted', 'awaiting'] as const;

function isKnownWithoutAReport(measure: EmailDailyMeasure | WhatsAppDailyMeasure): boolean {
  return (OUTCOME_MEASURES_KNOWN_WITHOUT_A_REPORT as readonly string[]).includes(measure);
}

/**
 * The count on a given day that would have been `threshold` of that day's
 * reported outcomes — the threshold line on a **counts** chart — or `null` where
 * no line belongs.
 *
 * This is how a rate threshold is drawn without a rate being computed. The line
 * is not flat: it rises and falls with the day's reported outcomes. On a day with
 * 1,200 messages delivered or bounced the 5% line sits at 60 bounces.
 *
 * **`null` on a day under {@link OVERVIEW_MIN_VOLUME_FOR_RATE}, and that reverses
 * this function's first version.** It drew the line on every day, arguing that a
 * line at 0.15 on a three-message day was "the true version of the 33% label":
 * the chart showed, without a number, that one bounce would clear it. Review
 * held that picture to the rule the rest of the page keeps. A bar ten times the
 * height of a red line labelled "Amazon reviews above 5%" says "this day is over
 * the line" as plainly as a percentage would — and the rate tile one card up
 * says of the same workspace "counts, not a percentage: a rate needs 1,000".
 * Amazon reviews a sending account on its rates over volume, not on a day of
 * three messages; the floor exists because a rate below it is noise, and a line
 * is a rate drawn rather than printed. So a day that does not clear the floor
 * gets its count and no line, and the chart says why.
 *
 * `null`, too, on a day with nothing to be a fraction of — no messages, or none
 * reported on. A line over a day whose every message is still awaiting an
 * outcome compares Amazon's threshold with nothing: that day's outcomes are
 * `null` in {@link emailDailyValues}, and the red line ran straight across the
 * gap, over a band that reads like a quiet day. Review found that as well.
 *
 * **Over the day's settled outcomes, since slice 19 — not its accepted count.**
 * The tiles moved to the settled denominator slice 18's halt uses, and a line
 * left on `accepted` would disagree with the tile beside it in the one state that
 * matters: a thousand accepted, a dozen reported, no bounce among them. On
 * accepted, the line sat at fifty over a bar of zero and said "far under
 * Amazon's line" about 988 messages nobody had reported on. On settled, a dozen
 * is under the floor and no line is drawn; a day earns its line with at least
 * {@link OVERVIEW_MIN_VOLUME_FOR_RATE} reported outcomes, and the line is the
 * threshold of those. `settled` is `delivered + bounced` on email, the same sum
 * `overviewSendingHealthSchema` divides by. WhatsApp has no line to draw — see
 * `chart.ts` — and its settled outcomes are `delivered + undeliverable`.
 *
 * And `null` on a day whose reported outcomes are failures alone while messages
 * still wait — {@link onlyBouncesReported}, the shape in which deliveries are not
 * being reported and `settled` is the bounces themselves. A line at 5% of those
 * would sit under a bar that is all of them.
 *
 * Fractional by design — it is a position on a scale, not a count of messages,
 * and rounding it to a whole message would move Amazon's line.
 */
export function dailyThresholdCount(
  bucket: EmailDailyBucket | WhatsAppDailyBucket,
  threshold: number,
): number | null {
  if (bucket.state !== 'activity') return null;
  const failed = 'bounced' in bucket ? bucket.bounced : bucket.undeliverable;
  const settled = bucket.delivered + failed;
  if (settled < OVERVIEW_MIN_VOLUME_FOR_RATE) return null;
  if (onlyBouncesReported({ delivered: bucket.delivered, bounced: failed, awaiting: bucket.awaiting })) {
    return null;
  }
  return settled * threshold;
}

/* -------------------------------------------------------------------------- */
/* Building the dense axis                                                     */
/* -------------------------------------------------------------------------- */

/** The `YYYY-MM-DD` UTC calendar day an instant falls in. */
export function utcCalendarDay(at: Date): CalendarDate {
  const year = String(at.getUTCFullYear()).padStart(4, '0');
  const month = String(at.getUTCMonth() + 1).padStart(2, '0');
  const day = String(at.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** The half-open instant span `[start, end)` a UTC calendar day covers. */
export function utcDayRange(day: CalendarDate): { readonly start: Date; readonly end: Date } {
  const start = new Date(`${day}T00:00:00.000Z`);
  return { start, end: new Date(start.getTime() + MS_PER_DAY) };
}

/**
 * Every UTC calendar day the half-open window `[since, until)` touches, in
 * order, including the days nothing happened on.
 *
 * **The dense axis is built here rather than by the query**, and that is the
 * point. A `GROUP BY` returns only the days with rows, and a chart built
 * straight from those draws thirty bars evenly spaced across a month with gaps
 * silently closed up — the classic time-series defect, where a quiet fortnight
 * becomes a smooth ramp. Every day in the window gets an entry; what differs is
 * which arm the entry is.
 *
 * A rolling thirty-day window starting mid-day touches thirty-one calendar days,
 * so this returns thirty-one entries for the default window. That is not an
 * off-by-one — see the file header on why the window is not rounded to whole
 * days — and it is why nothing anywhere assumes
 * {@link OVERVIEW_ACTIVITY_WINDOW_DAYS} is the bucket count.
 */
export function enumerateUtcDays(since: Date, until: Date): CalendarDate[] {
  if (until.getTime() <= since.getTime()) return [];
  const days: CalendarDate[] = [];
  let cursor = utcDayRange(utcCalendarDay(since)).start;
  while (cursor.getTime() < until.getTime()) {
    days.push(utcCalendarDay(cursor));
    cursor = new Date(cursor.getTime() + MS_PER_DAY);
  }
  return days;
}

/**
 * Whether a day is fully inside the span that was actually observed.
 *
 * `observedFrom` is `max(window.since, workspaceCreatedAt)` and `observedUntil`
 * is `window.until`. Folding the workspace's birthday into the same comparison
 * is deliberate: the day a workspace was created at 4pm is short for the same
 * reason today is short, and telling the reader "part of this day" twice in two
 * different ways would be two sentences for one fact.
 */
export function utcDayCoverage(
  day: CalendarDate,
  observedFrom: Date,
  observedUntil: Date,
): DailyBucketCoverage {
  const { start, end } = utcDayRange(day);
  const whole = start.getTime() >= observedFrom.getTime() && end.getTime() <= observedUntil.getTime();
  return whole ? 'whole_day' : 'partial_day';
}

/**
 * True when the whole UTC day had ended before this workspace was created.
 *
 * `<=` against the day's exclusive end, so the day a workspace was created on is
 * **not** before it: something could have been sent that afternoon, and marking
 * it undrawable would delete a real first day of sending.
 */
export function isBeforeWorkspace(day: CalendarDate, workspaceCreatedAt: Date): boolean {
  return utcDayRange(day).end.getTime() <= workspaceCreatedAt.getTime();
}

/* -------------------------------------------------------------------------- */
/* A channel's series — absent rather than thirty zeros                        */
/* -------------------------------------------------------------------------- */

/**
 * Why there is no chart for this channel.
 *
 * Every one of these could have been expressed as an array of thirty
 * `no_activity` buckets, and every one of them would then have drawn a flat line
 * along the baseline. That is true and useless: a flat line at zero is what a
 * healthy quiet week looks like *and* what a channel nobody has connected looks
 * like *and* what a workspace that has never sent anything looks like, and the
 * repair is different in all three cases. So the series is a union and this arm
 * has no bucket array — a component handed it has nothing to map over.
 *
 *   - `channel_not_connected` — there is no integration for this channel. The
 *     repair is on the Integrations screen and the dashboard says so;
 *   - `nothing_sent_ever` — connected, and nothing has ever been accepted on
 *     this channel. A new workspace, and the repair is to send something;
 *   - `nothing_sent_in_window` — this workspace has sent on this channel before
 *     and has sent nothing in the last thirty days. **This is the one worth
 *     noticing**, and it is separated from `nothing_sent_ever` for that reason:
 *     it is not a setup state, it is a workspace that has gone quiet, and the
 *     reader is the only one who knows whether that was on purpose.
 */
export const OVERVIEW_SERIES_UNAVAILABLE_VALUES = [
  'channel_not_connected',
  'nothing_sent_ever',
  'nothing_sent_in_window',
] as const;
export const overviewSeriesUnavailableSchema = z.enum(OVERVIEW_SERIES_UNAVAILABLE_VALUES);
export type OverviewSeriesUnavailable = z.infer<typeof overviewSeriesUnavailableSchema>;

/**
 * The sentence to print instead of a chart, per channel and reason.
 *
 * Per channel because "Amazon SES is not connected" and "WhatsApp is not
 * connected" send the reader to different places, and a generic "this channel"
 * makes them work out which one they are looking at. The same reason
 * `LIST_HEALTH_UNKNOWABLE_NOTE_BY_CHANNEL` is keyed that way in
 * `./list-safety.js`.
 *
 * Short by the text rule — these sit where a chart would have been, and a
 * paragraph in a chart's place is worse than the chart.
 */
export const OVERVIEW_SERIES_UNAVAILABLE_MESSAGES: Readonly<
  Record<Channel, Readonly<Record<OverviewSeriesUnavailable, string>>>
> = {
  email: {
    channel_not_connected: 'Amazon SES is not connected, so there is no email to chart yet.',
    nothing_sent_ever: 'No email has been sent from this workspace yet.',
    nothing_sent_in_window: `No email has been sent in the last ${OVERVIEW_ACTIVITY_WINDOW_DAYS} days.`,
  },
  whatsapp: {
    channel_not_connected: 'WhatsApp is not connected, so there is no WhatsApp to chart yet.',
    nothing_sent_ever: 'No WhatsApp message has been sent from this workspace yet.',
    nothing_sent_in_window: `No WhatsApp message has been sent in the last ${OVERVIEW_ACTIVITY_WINDOW_DAYS} days.`,
  },
};

/** The totals the bars add up to, carried so a component never sums them itself. */
export const emailSeriesTotalsSchema = z
  .object({
    accepted: z.number().int().min(0),
    delivered: z.number().int().min(0),
    bounced: z.number().int().min(0),
    complained: z.number().int().min(0),
    awaiting: z.number().int().min(0),
  })
  .strict();
export type EmailSeriesTotals = z.infer<typeof emailSeriesTotalsSchema>;

export const whatsAppSeriesTotalsSchema = z
  .object({
    accepted: z.number().int().min(0),
    delivered: z.number().int().min(0),
    undeliverable: z.number().int().min(0),
    awaiting: z.number().int().min(0),
  })
  .strict();
export type WhatsAppSeriesTotals = z.infer<typeof whatsAppSeriesTotalsSchema>;

/**
 * The no-chart arm, **per channel and not shared**.
 *
 * A single arm carrying `channel: channelSchema` was the obvious shape and it
 * has a hole in it: `channels.email` would then accept an object saying
 * `channel: 'whatsapp'`, parse cleanly, and render a WhatsApp sentence in the
 * email chart's place. Nothing would fail. `z.literal` on each side closes it,
 * so the object under `channels.email` is *about* email by the schema and not by
 * whoever wrote the mapper — the same reason the two activity arms carry
 * different numerator field names rather than a shared one plus discipline.
 */
const seriesUnavailableFields = {
  measured: z.literal(false),
  reason: overviewSeriesUnavailableSchema,
  /** The sentence to print. From {@link OVERVIEW_SERIES_UNAVAILABLE_MESSAGES}. */
  message: z.string(),
} as const;

export const emailSeriesUnavailableSchema = z
  .object({ ...seriesUnavailableFields, channel: z.literal('email') })
  .strict();
export type EmailSeriesUnavailable = z.infer<typeof emailSeriesUnavailableSchema>;

export const whatsAppSeriesUnavailableSchema = z
  .object({ ...seriesUnavailableFields, channel: z.literal('whatsapp') })
  .strict();
export type WhatsAppSeriesUnavailable = z.infer<typeof whatsAppSeriesUnavailableSchema>;

/**
 * The email chart's data.
 *
 * **`totals.accepted` must equal `GET /overview`'s `sending.accepted`**, exactly
 * — same cohort, same window, same `sent_at` predicate, same email-only filter —
 * and that is the invariant worth a verification assertion. A chart whose bars
 * do not add up to the tile above them teaches the reader that one of the two is
 * approximate.
 *
 * The totals are carried rather than summed in the component for the reason
 * `analytics.ts` carries `campaignCount` beside a capped list: a screen that
 * derives a total from what it happens to have been given says the wrong number
 * the day the list is shortened for any reason.
 */
export const emailDailySeriesSchema = z
  .object({
    measured: z.literal(true),
    channel: z.literal('email'),
    /** One entry per UTC day the window touches, in order, gaps included. */
    buckets: z.array(emailDailyBucketSchema),
    totals: emailSeriesTotalsSchema,
  })
  .strict();
export type EmailDailySeries = z.infer<typeof emailDailySeriesSchema>;

/** The WhatsApp chart's data. Meta's vocabulary throughout. */
export const whatsAppDailySeriesSchema = z
  .object({
    measured: z.literal(true),
    channel: z.literal('whatsapp'),
    buckets: z.array(whatsAppDailyBucketSchema),
    totals: whatsAppSeriesTotalsSchema,
  })
  .strict();
export type WhatsAppDailySeries = z.infer<typeof whatsAppDailySeriesSchema>;

export const emailSeriesSchema = z.discriminatedUnion('measured', [
  emailSeriesUnavailableSchema,
  emailDailySeriesSchema,
]);
export type EmailSeries = z.infer<typeof emailSeriesSchema>;

export const whatsAppSeriesSchema = z.discriminatedUnion('measured', [
  whatsAppSeriesUnavailableSchema,
  whatsAppDailySeriesSchema,
]);
export type WhatsAppSeries = z.infer<typeof whatsAppSeriesSchema>;

/**
 * Builds the no-chart arm with its sentence attached, so the two cannot drift.
 *
 * Two functions rather than one taking a `Channel`, for the reason the two
 * schemas are separate: a single builder returns a union, every caller then
 * narrows it, and the one that forgets puts a WhatsApp sentence where the email
 * chart should be. The channel is in the function's name, so the call site says
 * which chart it is filling.
 */
export function emailSeriesUnavailable(reason: OverviewSeriesUnavailable): EmailSeriesUnavailable {
  return {
    measured: false,
    channel: 'email',
    reason,
    message: OVERVIEW_SERIES_UNAVAILABLE_MESSAGES.email[reason],
  };
}

/** The WhatsApp half. See {@link emailSeriesUnavailable}. */
export function whatsAppSeriesUnavailable(
  reason: OverviewSeriesUnavailable,
): WhatsAppSeriesUnavailable {
  return {
    measured: false,
    channel: 'whatsapp',
    reason,
    message: OVERVIEW_SERIES_UNAVAILABLE_MESSAGES.whatsapp[reason],
  };
}

/* -------------------------------------------------------------------------- */
/* What has stopped itself and is waiting for a person                         */
/* -------------------------------------------------------------------------- */

/**
 * The campaign states that are **stopped and cannot start again without
 * somebody**.
 *
 * Not every non-terminal stop belongs here, and the exclusion is the decision.
 * `holding_between_waves` is slice 18's ordinary rhythm: the campaign is waiting
 * for outcomes and will release the next wave itself. Putting it on a dashboard
 * beside a halt would train the reader that this list is noise, and a quick
 * action beside it would lead somewhere they were not going and had no reason to
 * go — which is a worse tile than no tile.
 *
 * `satisfies` rather than a cast, so a status renamed in `../enums.js` fails to
 * compile here instead of quietly dropping out of the list.
 */
export const OVERVIEW_ATTENTION_STATUS_VALUES = [
  'halted_delivery_risk',
  'paused_insufficient_credits',
] as const satisfies readonly CampaignStatus[];
export const overviewAttentionStatusSchema = z.enum(OVERVIEW_ATTENTION_STATUS_VALUES);
export type OverviewAttentionStatus = z.infer<typeof overviewAttentionStatusSchema>;

/**
 * Why a campaign stopped, in the vocabulary the campaign screen already uses.
 *
 * Slice 18's four halt reasons, **derived** from
 * `CAMPAIGN_HALT_REASON_VALUES` rather than retyped, plus the one stop that is
 * not a halt: running out of credit. Retyping them would mean a fifth halt
 * reason added in `./list-safety.js` renders as a blank on this screen, which is
 * how a dashboard reports an urgent thing as nothing at all.
 */
export const OVERVIEW_ATTENTION_REASON_VALUES = [
  ...CAMPAIGN_HALT_REASON_VALUES,
  'insufficient_credits',
] as const;
export const overviewAttentionReasonSchema = z.enum(OVERVIEW_ATTENTION_REASON_VALUES);
export type OverviewAttentionReason = z.infer<typeof overviewAttentionReasonSchema>;

/**
 * The dashboard's sentence for each stop — **short**, and deliberately not
 * `CAMPAIGN_HALT_SENTENCES`.
 *
 * Slice 18's sentences are three to four lines each and end with what to do
 * about it. That is right on the campaign screen, where the reader has arrived
 * to deal with this one campaign. It is wrong on a dashboard row, where four of
 * them stack into a wall nobody reads — and the text rule is explicit that
 * honesty about a number stays visible in four words rather than a paragraph.
 *
 * So the row says what happened, the link goes to the campaign, and the campaign
 * screen says what to do. Progressive, not deleted: the long sentence is still
 * printed, one click away, from the same `CampaignHaltReason` code carried here.
 * Nothing on this screen is the only place a fact appears.
 */
export const OVERVIEW_ATTENTION_SUMMARIES: Readonly<Record<OverviewAttentionReason, string>> = {
  email_hard_bounce_rate: 'Stopped itself — too many addresses did not exist.',
  whatsapp_undeliverable_rate: 'Stopped itself — too many numbers could not be reached.',
  risk_unmeasurable: 'Stopped itself — no delivery reports came back.',
  plan_unreadable: 'Stopped — its send-in-batches plan could not be read.',
  insufficient_credits: 'Paused — not enough credits to finish.',
};

/**
 * A campaign that has stopped and is waiting for a person.
 *
 * `stoppedAt` is nullable and that is not laziness: `paused_insufficient_credits`
 * is written by the send runner and `halted_delivery_risk` by the halt check, and
 * a campaign whose stop predates the column carrying the instant has no honest
 * value to report. Null means "we do not know when", which the row prints as
 * nothing rather than as a fabricated timestamp — the same rule
 * `OverviewSnapshot.checkedAt` follows.
 *
 * There are no counts on this row. The two numbers a reader would want —
 * how many failed, out of how many — are on the campaign screen with the
 * volume caveat beside them, and a bare "3 of 51" on a dashboard row is the
 * percentage-from-a-tiny-denominator problem with the division left to the
 * reader.
 */
export const stoppedCampaignSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    channel: channelSchema,
    status: overviewAttentionStatusSchema,
    reason: overviewAttentionReasonSchema,
    /** The short line. From {@link OVERVIEW_ATTENTION_SUMMARIES}. */
    summary: z.string(),
    /** Slice 18's code, so the campaign screen prints its full sentence. Null for a credit pause. */
    haltReason: campaignHaltReasonSchema.nullable(),
    /** When it stopped. Null when nothing recorded the instant — never guessed. */
    stoppedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type StoppedCampaign = z.infer<typeof stoppedCampaignSchema>;

/**
 * How many stopped campaigns the dashboard lists. Four.
 *
 * Small on purpose. This block is above the fold and its job is to be noticed,
 * and a list of twenty stopped campaigns is a screen somebody scrolls past. Four
 * fits beside the charts on a laptop; `total` says how many there really are, so
 * the block can say "4 of 11" and link to the campaigns list.
 */
export const OVERVIEW_ATTENTION_LIMIT = 4;

/**
 * The urgent block. **Empty is the normal case and must render as nothing.**
 *
 * Not "All campaigns running", not a green tick, not a card saying there is
 * nothing to report. A reassurance tile occupying the most valuable space on the
 * screen on the 364 days it has nothing to say is how the reader learns to skip
 * that space on the day it does. `total: 0` renders no block at all.
 *
 * `total` is uncapped while `campaigns` is capped, on `analytics.ts`'s
 * precedent: a truncated total is a wrong total, and a screen that derives the
 * count from `campaigns.length` says four when eleven campaigns are stopped.
 */
export const overviewAttentionSchema = z
  .object({
    /** Every stopped campaign in this workspace. Uncapped, and the number shown. */
    total: z.number().int().min(0),
    /** At most {@link OVERVIEW_ATTENTION_LIMIT}, most recently stopped first. */
    campaigns: z.array(stoppedCampaignSchema),
  })
  .strict();
export type OverviewAttention = z.infer<typeof overviewAttentionSchema>;

/** Builds a row with its short sentence attached, so the two cannot drift. */
export function stoppedCampaign(input: {
  readonly id: string;
  readonly name: string;
  readonly channel: Channel;
  readonly status: OverviewAttentionStatus;
  readonly reason: OverviewAttentionReason;
  readonly haltReason: CampaignHaltReason | null;
  readonly stoppedAt: string | null;
}): StoppedCampaign {
  return {
    id: input.id,
    name: input.name,
    channel: input.channel,
    status: input.status,
    reason: input.reason,
    summary: OVERVIEW_ATTENTION_SUMMARIES[input.reason],
    haltReason: input.haltReason,
    stoppedAt: input.stoppedAt,
  };
}

/* -------------------------------------------------------------------------- */
/* The response                                                                */
/* -------------------------------------------------------------------------- */

/**
 * How the buckets were made, carried on the wire so the chart states it rather
 * than assumes it.
 *
 * `unit` and `timeZone` are literals: a response claiming hourly buckets or a
 * local zone does not parse. `axisLabel` rides along so the chart's x-axis title
 * comes from the same object the buckets did — the alternative is a component
 * with `"Day (UTC)"` typed into it, which is a caption that goes stale the day
 * somebody changes the bucketing and forgets the second literal.
 *
 * `bucketedBy` names the column, in the words the rest of this codebase uses for
 * it, because "which stamp put this message in this bar" is the question a
 * reader who finds a discrepancy will ask and the answer is not guessable from
 * the shape.
 */
export const overviewActivityBucketingSchema = z
  .object({
    unit: z.literal(OVERVIEW_ACTIVITY_BUCKET_UNIT),
    timeZone: z.literal(OVERVIEW_ACTIVITY_TIME_ZONE),
    /** The x-axis title the chart must print. From {@link OVERVIEW_ACTIVITY_TIME_ZONE_COPY}. */
    axisLabel: z.string(),
    /** `messages.sent_at` — the acceptance stamp. See the file header. */
    bucketedBy: z.literal('messages.sent_at'),
  })
  .strict();
export type OverviewActivityBucketing = z.infer<typeof overviewActivityBucketingSchema>;

/** The bucketing block, built once so no component can describe it differently. */
export const OVERVIEW_ACTIVITY_BUCKETING: OverviewActivityBucketing = {
  unit: OVERVIEW_ACTIVITY_BUCKET_UNIT,
  timeZone: OVERVIEW_ACTIVITY_TIME_ZONE,
  axisLabel: OVERVIEW_ACTIVITY_TIME_ZONE_COPY.axisLabel,
  bucketedBy: 'messages.sent_at',
};

/**
 * `GET /overview/activity`. The charts, and the one thing above them that is
 * urgent.
 *
 * **Same window as `GET /overview`, same `generatedAt` semantics, different
 * request.** The two are read independently and each carries its own timestamp,
 * so nothing on the screen implies the tiles and the bars came from one instant
 * — the property `HomePage.tsx` already states about the overview and the
 * engagement panel. What *is* guaranteed is that the window bounds are computed
 * from the same constant by the same function, so the bars and the tiles
 * describe the same thirty days even when they were read a second apart.
 *
 * `workspaceCreatedAt` is on the response rather than left implicit because it
 * is the only thing that explains a `before_workspace` bucket. Without it the
 * screen can draw the gap but cannot say why it is there, and an unexplained gap
 * in a chart reads as a fault in the product.
 *
 * `channels` is an object keyed on the two channels rather than an array,
 * deliberately: an array can be short, and a WhatsApp series missing from an
 * array renders as nothing at all with no arm saying why. A keyed object cannot
 * omit a channel — the same argument `overviewSendabilitySchema` makes by being
 * `{ ses, whatsapp }`.
 *
 * `.strict()` is doing the same work it does on `overviewResponseSchema`: no
 * open rate, no click rate, no WhatsApp read count, and no per-day percentage
 * can be added by a well-meaning mapper without this file changing first.
 */
export const overviewActivityResponseSchema = z
  .object({
    generatedAt: isoDateTimeSchema,
    /** Identical bounds to `GET /overview`. See the file header. */
    window: overviewWindowSchema,
    bucketing: overviewActivityBucketingSchema,
    /** Why the oldest buckets may carry nothing. Never null — every workspace has one. */
    workspaceCreatedAt: isoDateTimeSchema,
    channels: z
      .object({
        email: emailSeriesSchema,
        whatsapp: whatsAppSeriesSchema,
      })
      .strict(),
    attention: overviewAttentionSchema,
  })
  .strict();
export type OverviewActivityResponse = z.infer<typeof overviewActivityResponseSchema>;
